import React, { useCallback, useMemo, useContext } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import { replace } from 'connected-react-router'

import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import HeadData from 'components/Common/HeadData'
import config from 'constants/config'

import { LUXURY_ESCAPES } from 'constants/brands'

import { OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import * as InteractionStudioService from 'api/interactionStudio'

import Heading from 'components/Luxkit/Typography/Heading'
import OfferListFrontPageFilters from 'components/OfferList/OfferListFilter/OfferListFrontPageFilters'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextLink from 'components/Luxkit/TextLink'

import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { useGlobalToursSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalToursSearch'
import OfferList, { OfferListV2Item } from 'components/OfferList/v2/OfferList'
import LayoutContainer from 'components/Common/LayoutContainer'
import GeoContext from 'contexts/geoContext'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/v2/OfferListEventsContext'
import tourV2VerticalNameSelector from 'selectors/TourV2/tourV2VerticalNameSelector'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { EnabledFilters } from 'components/Search/type'
import { tourSortByTypes } from 'constants/offerListFilters'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LineAngleLeftIcon from 'components/Luxkit/Icons/line/LineAngleLeftIcon'
import { getListName, productClickSnowplow } from 'analytics/enhanced-ecommerce'
import { productClickObject } from 'analytics/eventDefinitions'
import uuidV4 from 'lib/uuidV4'

const OffersContainer = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.neutral.eight};
`

const OfferListWrapper = styled(LayoutContainer)`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding-bottom: ${rem(16)};
  padding-top: ${rem(16)};
`

const NoOffersMessage = styled.div`
  padding: ${rem(24)} 0;

  > * + * {
    margin-top: ${rem(8)};
  }
`

interface MappedStateProps {
  currency: string
  urlFilters: App.OfferListFilters
  toursVerticalName: string,
  standaloneCruiseEnabled: boolean,
}

interface Props { }

const filterTypes: Array<App.OfferListOfferType> = [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2]

const enabledFilters: EnabledFilters = {
  holidayTypes: true,
  amenities: false,
  inclusions: false,
  locations: true,
  customerRating: false,
  priceFilter: false,
  offerTypes: false,
}

function ToursPage(props: Props & MappedStateProps) {
  const {
    currency,
    urlFilters,
    toursVerticalName,
    standaloneCruiseEnabled,
  } = props
  const isManualSortingEnabled = isManualSortingEnabledFor('tours')
  const { currentRegionCode } = useContext(GeoContext)

  const dispatch = useAppDispatch()

  const resetFilters = useCallback(() => {
    dispatch(replace({}))
  }, [dispatch])

  const filters = useMemo((): App.OfferListFilters => {
    if (isManualSortingEnabled && urlFilters.locations?.length === 0) {
      return {
        ...urlFilters,
        offerTypes: [OFFER_TYPE_TOUR_V2],
        sortBy: 'vertical-tour',
        limit: 250,
      }
    }
    const finalFilters = {
      ...urlFilters,
      offerTypes: filterTypes,
      frontPageVisibleOnly: true,
    }
    if (standaloneCruiseEnabled) {
      finalFilters.holidayTypes = ['!Cruises']
    }
    return finalFilters
  }, [urlFilters, standaloneCruiseEnabled, isManualSortingEnabled])

  const offerListExtras: Array<OfferListV2Item> = useMemo(() => {
    const extras: Array<OfferListV2Item> = [
      {
        element: <NoOffersMessage>
          <Heading variant="heading3">No offers available</Heading>
          <BodyText variant="large">There are no offers matching your selected filters.</BodyText>
          <TextLink onClick={resetFilters}>Reset filters</TextLink>
        </NoOffersMessage>,
        id: 'no-offers-message',
        position: 2,
        show: 'hasNoOffers',
      }]
    return extras
  }, [resetFilters])

  const headTitle = config.BRAND === LUXURY_ESCAPES ?
    `Luxury ${toursVerticalName}` :
    `${toursVerticalName}`
  const description = config.BRAND === LUXURY_ESCAPES ?
    'Embark on a life changing tour or cruise. Curated by travel experts to save you time. Featuring luxury accommodation, select dining and exclusive inclusions.' :
    undefined

  const { globalToursSearchDispatch, globalToursSearchState } = useGlobalToursSearchContext()

  const listId = useMemo(() => uuidV4(), [])
  const onOfferListAnalyticEvent = useCallback((action: OfferListEvents, data: any) => {
    const listName = getListName('OfferList', 'Tour Exclusive', 'Top Exclusive Offers page')
    if (action === OfferListEvents.productClick) {
      const { offer, index } = data
      InteractionStudioService.trackOfferListClick({
        offerId: offer.id,
        index,
        list: 'tours',
      })
      productClickSnowplow(currency, productClickObject(offer, index + 1), { listId, listName })
    }
  }, [currency, listId])

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage} >
    <div>
      <HeadData
        title={`${headTitle} - ${config.title} ${currentRegionCode}`}
        description={description}
      />
      <GlobalSearchContextProvider
        state={globalToursSearchState}
        dispatch={globalToursSearchDispatch}
      >
        <OffersContainer>
          <OfferListWrapper>
            <VerticalSpacer gap={16}>
              <CSSBreakpoint max="sm">
                <TextLink startIcon={<LineAngleLeftIcon />} to="/tours">All tours</TextLink>
              </CSSBreakpoint>
              <Heading variant="heading2" align="start">
                Today's top <i>exclusive</i> offers
              </Heading>
              <OfferListFrontPageFilters
                types={filterTypes}
                existingFilters={filters}
                enabledFilters={enabledFilters}
                sortOptions={tourSortByTypes}
              />
            </VerticalSpacer>
            <OfferListEventsProvider onListEvent={onOfferListAnalyticEvent}>
              <OfferList
                filters={filters}
                interstitials={offerListExtras}
                tileStyle="hero"
                isHeroListView
              />
            </OfferListEventsProvider>
          </OfferListWrapper>
        </OffersContainer>
      </GlobalSearchContextProvider>
    </div>
  </AnalyticsPageContext.Provider>
}

export default connect<MappedStateProps, void, Props, App.State>((state) => {
  return {
    currency: state.geo.currentCurrency,
    urlFilters: getUrlOfferListFilters(state),
    toursVerticalName: tourV2VerticalNameSelector(state),
    standaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
  }
})(ToursPage)
